import { createClient } from '../client/browser'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { QuizData } from './quiz'

/**
 * Saves quiz data temporarily before user authentication
 * Uses API route with service role to bypass RLS
 */
export async function saveTempQuizData(
  data: QuizData
): Promise<{ success: boolean; tempId?: string; error?: string }> {
  try {
    // Call API endpoint that has service role access
    const response = await fetch('/api/quiz/temp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    })
    
    const result = await response.json()
    
    if (!response.ok || !result.success) {
      console.error('Error creating temp quiz:', result.error)
      return { 
        success: false, 
        error: result.error || 'Failed to save quiz data temporarily' 
      }
    }
    
    console.log('Successfully saved temp quiz to database with ID:', result.tempId)
    return { success: true, tempId: result.tempId }
    
  } catch (error) {
    console.error('Unexpected error saving temp quiz data:', error)
    return { 
      success: false, 
      error: 'An unexpected error occurred' 
    }
  }
}

/**
 * Associates a temporary quiz with an authenticated user and processes matching
 */
export async function completeTempQuiz(
  tempId: string,
  userId: string,
  supabase?: SupabaseClient
): Promise<{ success: boolean; quizId?: string; quizData?: { type: string; tier: string; rate: number }; error?: string }> {
  const client = supabase || createClient()
  
  try {
    // First, get the temp quiz data
    const { data: tempQuiz, error: fetchError } = await client
      .from('matching_quizzes')
      .select('*')
      .eq('id', tempId)
      .eq('status', 'pending_auth')
      .single()
    
    if (fetchError || !tempQuiz) {
      console.error('Temp quiz not found:', fetchError)
      return { 
        success: false, 
        error: 'Quiz session expired. Please retake the quiz.' 
      }
    }
    
    // Update the quiz with the user ID
    const { error: updateError } = await client
      .from('matching_quizzes')
      .update({
        userId,
        status: 'pending',
        updatedAt: new Date().toISOString()
      })
      .eq('id', tempId)
    
    if (updateError) {
      console.error('Error updating quiz with user:', updateError)
      return { 
        success: false, 
        error: 'Failed to associate quiz with user' 
      }
    }
    
    // Try to update user's timezone if they exist, but don't fail if they don't
    if (tempQuiz.timezone) {
      const { error: timezoneError } = await client
        .from('users')
        .update({
          timezone: tempQuiz.timezone,
          updatedAt: new Date().toISOString()
        })
        .eq('id', userId)
      
      if (timezoneError) {
        // User might not exist in users table yet, that's okay
        console.log('Could not update user timezone (user may not exist in users table yet):', timezoneError.message)
      } else {
        console.log('Updated user timezone to:', tempQuiz.timezone)
      }
    }
    
    // Now find matching pros — enable BES top-up for client-app quizzes.
    const { findMatchingPros, updateMatchingQuizWithBesPros, updateMatchingQuizWithPros } = await import('./matching')

    const matchingCriteria = {
      sessionType: tempQuiz.type as 'individual' | 'couples',
      therapistTier: tempQuiz.tier as 'resident' | 'associate',
      rate: tempQuiz.rate,
      timezone: tempQuiz.timezone,
      availability: tempQuiz.times,
      insurance: tempQuiz.insurance,
      province: tempQuiz.province ?? undefined,
    }

    // BES top-up: associate-tier client-app quizzes only.
    // Skip for resident tier (rate mismatch) and for partnership flows
    // (imwell / mhap / employer stay on the partner's covered pool).
    const includeBesFallback = tempQuiz.tier === 'associate' && !tempQuiz.partnershipId

    const { proIds, besProIds, besMatchReasons, error: matchingError } = await findMatchingPros(
      matchingCriteria,
      client,
      { includeBesFallback },
    )

    if (matchingError) {
      console.error('Error finding matches:', matchingError)
    }

    // Update the quiz with matched internal pro IDs
    if (proIds && proIds.length > 0) {
      await updateMatchingQuizWithPros(tempId, proIds, client)
    }

    // Record surfaced BES pros on the quiz row itself. No bes_referrals
    // row is created here — that only happens when the client submits
    // an actual inquiry via the /matched flow. besMatchReasons rides
    // alongside so request-consult can hydrate criteria.matchReasons.
    if (besProIds && besProIds.length > 0) {
      await updateMatchingQuizWithBesPros(tempId, besProIds, besMatchReasons ?? {}, client)
    }
    
    return {
      success: true,
      quizId: tempId,
      quizData: {
        type: tempQuiz.type,
        tier: tempQuiz.tier,
        rate: tempQuiz.rate
      }
    }
    
  } catch (error) {
    console.error('Unexpected error completing temp quiz:', error)
    return { 
      success: false, 
      error: 'An unexpected error occurred' 
    }
  }
}